import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    inject,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import {
    ClientManagerInstanceToken,
    ClientManagerInterface,
    ConfigConstants,
    ConfigService,
    isDefined,
    SignaturePad,
    SignaturePadOptions,
} from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    exhaustMap,
    filter,
    map,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateFunctionalElementSelection,
    updateSignature,
    updateSignatureName,
} from '../../store/actions/functional-elements.actions';
import {
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormSupportParticipantCopies,
    getFormTypeFilters,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { ContentId } from '../../types/content-block';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { Signature } from '../../types/signature';
import { isAstoriaFunctionalElementVisible, isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';

@Component({
    selector: 'pace-forms-signature',
    templateUrl: './signature.component.html',
    styleUrls: ['./signature.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SignatureComponent implements OnInit, AfterViewInit, OnDestroy {
    private readonly configService = inject(ConfigService);
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    @ViewChild('signatureCanvas')
    public signatureCanvas: ElementRef;

    public signaturePad: SignaturePad | undefined;

    public readOnly$: Observable<boolean>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public signature$: Observable<Signature>; // if a content id is given
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public controllingCheckpointOptions$: Observable<RadioOption[] | undefined>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public visible$: Observable<boolean>;
    public showEmailField$: Observable<boolean>;
    public supportParticipantCopies$: Observable<boolean>;
    public displayHistoryButton$: Observable<boolean>;

    private destroy$ = new Subject<void>();
    private signatureInitializedSubject = new Subject<void>();

    public signatureForm: FormGroup;
    public firstNameControl: FormControl;
    public lastNameControl: FormControl;
    public emailControl: FormControl;
    public radioGroup: FormGroup;
    public radioControl: FormControl;

    public FUNCTIONAL_ELEMENT_TYPE = FUNCTIONAL_ELEMENT_TYPE;
    public faCheck = faCheck;

    private participantCopyIdForCurrentSignature = '';
    private emailPattern = '^([a-zA-Z0-9\\+_\\-]+)(\\.[a-zA-Z0-9\\+_\\-]+)*@([a-zA-Z0-9\\-]+\\.)+[a-zA-Z]{2,6}$';
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        @Inject(ClientManagerInstanceToken) private readonly clientManager: ClientManagerInterface,
        private readonly formBuilder: FormBuilder,
        private readonly changeDetectorRef: ChangeDetectorRef,
        public readonly store: Store<FormsState>,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
        this.supportParticipantCopies$ = store.select(getFormSupportParticipantCopies);
    }

    public ngOnInit(): void {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.radioControl = this.formBuilder.control('', [Validators.required]);
        this.radioGroup = this.formBuilder.group({ radioControl: this.radioControl });

        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.firstNameControl = this.formBuilder.control('', [Validators.required]);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.lastNameControl = this.formBuilder.control('', [Validators.required]);
        // eslint-disable-next-line @typescript-eslint/unbound-method
        this.emailControl = this.formBuilder.control('', [Validators.required, Validators.pattern(this.emailPattern)]);
        this.signatureForm = this.formBuilder.group(
            {
                radioGroup: this.radioGroup,
                firstName: this.firstNameControl,
                lastName: this.lastNameControl,
                email: this.emailControl,
            },
            { validators: () => this.checkSignatureValidity() },
        );

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.signatureForm);
                    this.updateSignatureValidity();
                })
                .catch((error: Error) => {
                    this.log.error(`SignatureComponent: ngOnInit: Error in parentForm.push: ${JSON.stringify(error)}`);
                });
        }

        if (typeof this.id !== 'undefined') {
            this.signature$ = this.store
                .select(getFormFunctionalElementByIdFactory<Signature>({ id: this.id }))
                .pipe(filter(isDefined));
        } else {
            throw new Error(`SignatureComponent: ngOnInit: cannot continue without content ID of signature!`);
        }

        this.showEmailField$ = this.signature$.pipe(
            withLatestFrom(this.supportParticipantCopies$),
            map(
                ([signature, supportParticipantCopies]) =>
                    (signature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER ||
                        signature.type === FUNCTIONAL_ELEMENT_TYPE.APPROVER_SIGNATURE) &&
                    supportParticipantCopies,
            ),
        );

        this.controllingCheckpoint$ = this.signature$.pipe(
            switchMap((signature) =>
                signature.controllingCheckpoint.length > 0
                    ? this.store
                          .select(getFormCheckpointByXmlIdFactory({ xmlId: signature.controllingCheckpoint }))
                          .pipe(take(1))
                    : of(undefined),
            ),
        );

        this.controllingCheckpointOptions$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.displayHistoryButton$ = this.signature$.pipe(
            map(
                (signature) =>
                    isDefined(signature.uniqueId) &&
                    signature.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );

        this.visible$ = combineLatest([
            this.signature$,
            this.controllingCheckpoint$,
            this.typeFilters$,
            this.formFilters$,
            this.controllingCheckpointOptions$,
        ]).pipe(
            map(([signature, checkpoint, typeFilters, formFilters, options]) => {
                // choices are available only in cosima, and controllingCheckpoints only in astoria
                if (isDefined(signature.choices)) {
                    return isCosimaFunctionalElementVisible(signature, typeFilters, formFilters, signature.elementType);
                } else if (isDefined(checkpoint)) {
                    return isAstoriaFunctionalElementVisible(
                        signature,
                        checkpoint.elementType,
                        typeFilters,
                        formFilters,
                        { [checkpoint.xmlId]: options },
                    );
                }

                this.log.debug(
                    `SignatureComponent: cannot correctly detect visibility from filters, displaying it anyway for element [${signature}] and checkpoint [${checkpoint}]`,
                );
                return true;
            }),
        );

        this.firstNameControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of signature$
                withLatestFrom(this.signature$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, signature]: [string, Signature]) => {
                const firstName = newValue;
                const lastName = this.lastNameControl.value as string;
                const email = this.emailControl.value as string;
                this.store.dispatch(
                    updateSignatureName({
                        id: signature.id,
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName,
                        lastName,
                        email,
                    }),
                );
            });

        this.lastNameControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of signature$
                withLatestFrom(this.signature$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, signature]: [string, Signature]) => {
                const firstName = this.firstNameControl.value as string;
                const lastName = newValue;
                const email = this.emailControl.value as string;
                this.store.dispatch(
                    updateSignatureName({
                        id: signature.id,
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName,
                        lastName,
                        email,
                    }),
                );
            });

        this.emailControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                filter(() => this.emailControl.valid),
                withLatestFrom(this.signature$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, signature]: [string, Signature]) => {
                const firstName = this.firstNameControl.value as string;
                const lastName = this.lastNameControl.value as string;
                const email = newValue;
                this.store.dispatch(
                    updateSignatureName({
                        id: signature.id,
                        signatureType: signature.type,
                        xmlId: signature.nameXmlId,
                        firstName,
                        lastName,
                        email,
                    }),
                );
                this.changeDetectorRef.markForCheck();
                this.changeDetectorRef.detectChanges();
            });

        this.signatureInitializedSubject
            .asObservable()
            .pipe(
                exhaustMap(() =>
                    combineLatest([this.signature$, this.readOnly$]).pipe(
                        withLatestFrom(this.selectedParticipant$, this.controllingCheckpointOptions$),
                        tap(
                            ([[signature, readOnly], selectedParticipant, radioOptions]: [
                                [Signature, boolean],
                                ParticipantCopyId,
                                RadioOption[] | undefined,
                            ]) => {
                                this.firstNameControl[signature.disabled || readOnly ? 'disable' : 'enable']({
                                    emitEvent: false,
                                    onlySelf: true,
                                });
                                this.lastNameControl[signature.disabled || readOnly ? 'disable' : 'enable']({
                                    emitEvent: false,
                                    onlySelf: true,
                                });
                                this.emailControl[signature.disabled || readOnly ? 'disable' : 'enable']({
                                    emitEvent: false,
                                    onlySelf: true,
                                });

                                this.firstNameControl.patchValue(signature.firstName, { onlySelf: true });
                                this.lastNameControl.patchValue(signature.lastName, { onlySelf: true });
                                this.emailControl.patchValue(signature.email, { onlySelf: true });

                                if (signature.emailRequired) {
                                    this.emailControl.setValidators([
                                        // eslint-disable-next-line @typescript-eslint/unbound-method
                                        Validators.required,
                                        Validators.pattern(this.emailPattern),
                                    ]);
                                } else {
                                    this.emailControl.clearValidators();
                                }

                                const selectedOption =
                                    typeof radioOptions !== 'undefined'
                                        ? radioOptions.find((o) => o.selected)
                                        : undefined;
                                const selectedOptionType =
                                    typeof selectedOption !== 'undefined' ? selectedOption.optionType : undefined;

                                // ha ndling cosima choices
                                if (signature.choices) {
                                    const selectedChoice = Object.values(signature.choices).find(
                                        (choice) => choice?.selected,
                                    );

                                    if (isDefined(selectedChoice)) {
                                        this.radioControl.patchValue(selectedChoice.xmlId);
                                    } else {
                                        this.radioControl.reset('');
                                    }

                                    this.radioControl[readOnly ? 'disable' : 'enable']({
                                        emitEvent: false,
                                    });
                                }

                                if (
                                    // re store signature when we switch the selected participant
                                    selectedParticipant !== this.participantCopyIdForCurrentSignature ||
                                    // re store signature when we restore a saved state: Then the signature pad is still empty, but
                                    // th e stored signature state contains data
                                    (this.signaturePad?.isEmpty && signature.signatureDataPoints.length > 0) ||
                                    // re store signature (to nothing, set by the reducer) when NA is selected
                                    selectedOptionType === RADIO_OPTION_TYPE.NA
                                ) {
                                    this.signaturePad?.off();
                                    this.signaturePad?.restoreFromDataPoints(signature.signatureDataPoints);
                                    this.participantCopyIdForCurrentSignature = selectedParticipant;
                                } else if (
                                    signature.signature.length === 0 &&
                                    signature.signatureDataPoints.length === 0
                                ) {
                                    this.signaturePad?.clear();
                                }
                                if (signature.disabled || readOnly) {
                                    this.signaturePad?.off();
                                } else {
                                    this.signaturePad?.on();
                                }

                                this.updateSignatureValidity();
                            },
                        ),
                    ),
                ),
                takeUntil(this.destroy$),
            )
            .subscribe();

        this.radioControl.valueChanges
            .pipe(distinctUntilChanged(), withLatestFrom(this.signature$), takeUntil(this.destroy$))
            .subscribe(([value, signature]: [string | null, Signature]) => {
                this.store.dispatch(
                    updateFunctionalElementSelection({
                        id: signature.id,
                        selectedChoiceXmlId: isDefined(value) ? value : undefined,
                    }),
                );
            });
    }

    public ngAfterViewInit(): void {
        const signatureOptions: SignaturePadOptions = {
            outputImageType: 'image/jpeg',
            outputImageMaxSize: 9000,
            backgroundColor: 'rgba(255, 255, 255, 1)',
            onEnd: () => this.updateSignature(),
        };

        this.signaturePad = new SignaturePad(
            this.log,
            <HTMLCanvasElement>this.signatureCanvas.nativeElement,
            signatureOptions,
        );

        this.signatureInitializedSubject.next();
    }

    public updateSignature(): void {
        if (typeof this.signaturePad === 'undefined') {
            throw new Error(`SignatureComponent: ngAfterViewInit: signaturePad is undefined`);
        }
        combineLatest([this.signature$, this.signaturePad.dataUrl$, this.signaturePad.dataPoints$])
            .pipe(take(1))
            .subscribe(([signature, dataUrl, dataPoints]) => {
                this.store.dispatch(
                    updateSignature({
                        id: signature.id,
                        signatureType: signature.type,
                        signatureDataPoints: dataPoints,
                        newSignature: dataUrl,
                        dateXmlId: signature.dateXmlId,
                        date: isDefined(signature.choices)
                            ? moment().format(ConfigConstants.formatPatternDateCosima)
                            : moment().format(this.clientManager.getDateFormat(false)),
                    }),
                );
            });
    }

    public ngOnDestroy(): void {
        // re move form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.signatureForm);
            this.parentForm.removeAt(index);
        }
        this.destroy$.next();
    }

    public clearSignature(): void {
        if (typeof this.signaturePad === 'undefined') {
            throw new Error(`SignatureComponent: clearSignature: Trying to clear non-existent signature pad!`);
        }
        this.signaturePad.clear();
        this.updateSignatureValidity();

        this.signature$.pipe(take(1)).subscribe((signature) =>
            this.store.dispatch(
                updateSignature({
                    id: signature.id,
                    signatureType: signature.type,
                    signatureDataPoints: [],
                    newSignature: '',
                    dateXmlId: signature.dateXmlId,
                    date: '',
                }),
            ),
        );
    }

    public updateSignatureValidity(): void {
        this.signatureForm.updateValueAndValidity();
        this.changeDetectorRef.markForCheck();
        this.changeDetectorRef.detectChanges();
    }

    private checkSignatureValidity(): null | ValidationErrors {
        const errors: ValidationErrors = {};

        if (this.signaturePad?.isEmpty) {
            errors['signature-missing'] = 'Signature is missing';
        }

        return Object.keys(errors).length > 0 ? errors : null;
    }

    public reset(): void {
        this.radioControl.reset();
    }
}
